import { useClient, useQuery } from '@weenat/client'
import { useArvalisClientIdFromOrg } from '@weenat/client/dist/core/dss/waterBalance'
import { Universe } from '@weenat/client/dist/core/universes'
import { BillingStatus } from '@weenat/client/dist/enums'
import useFilteredSummaries from '@weenat/client/dist/hooks/useFilteredSummaries'
import { PlotOrStationSummary } from '@weenat/client/dist/resources/measurements'
import { useOrgContext } from 'app/orgProvider'
import { Redirect, useSearchParams } from 'app/routx-router'
import { useBackgroundMapContext } from 'app/src/dashboard/components/DashboardMap/contexts/BackgroundMapContext'
import usePODClusters from 'app/src/dashboard/components/DashboardMap/hooks/usePODClusters'
import PlotOrStationList from 'app/src/dashboard/components/DashboardMap/PlotOrStationList'
import PlotsMapOverlay from 'app/src/dashboard/components/DashboardMap/PlotsMapOverlay'
import useUniverseBackgroundMapContext from 'app/src/dashboard/components/DashboardMap/universes/useUniverseBackgroundMapContext'
import FeatGuarded from 'app/src/kit/FeatGuarded'
import LoadingCircle from 'app/src/kit/loaders/LoadingCircle'
import DisplaySettings from 'app/src/myFarm/tools/DisplaySettings'
import MyFarmTools from 'app/src/myFarm/tools/MyFarmTools'
import { useUniverse, useUniverseHorizon } from 'app/state'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import { useMemo } from 'react'

// 10 Minutes
// Might need to change when frost universe is active
const REFETCH_INTERVAL = 10 * 60 * 1000
const EMPTY_SUMMARIES: PlotOrStationSummary[] = []

const RedesignedIndex = () => {
  const client = useClient()
  const [universe] = useUniverse()
  const { focusedMetricId } = useUniverseBackgroundMapContext()
  const org = useOrgContext()
  const currentOrgId = org.id
  const preferencesQuery = useQuery(client.preferences.get())

  const isExpertOrg = !isNil(org) ? org.billingStatus === BillingStatus.expert : false
  const [focusedHorizon] = useUniverseHorizon()
  const [searchParams] = useSearchParams()

  const { search } = useBackgroundMapContext()

  const summariesQueryParams = useMemo(
    () => ({
      organizationId: currentOrgId,
      universe: universe !== Universe.overview ? universe : undefined
    }),
    [currentOrgId, universe]
  )

  const clientCode = useArvalisClientIdFromOrg(org)

  const summariesRequester = useMemo(
    () =>
      universe === Universe.irrelis
        ? client.waterBalanceArvalis.getSummaries({
            orgId: currentOrgId,
            clientCode,
            farmer: org.meta?.corhize_irrelis_farmer ?? undefined
          })
        : client.measurements.plotsAndStations.summaries.getAllPages(summariesQueryParams),
    [
      client.measurements.plotsAndStations.summaries,
      client.waterBalanceArvalis,
      clientCode,
      currentOrgId,
      summariesQueryParams,
      universe,
      org.meta?.corhize_irrelis_farmer
    ]
  )

  const summariesRequest = useQuery(summariesRequester, {
    enabled: !isNil(currentOrgId),
    // Every 10 minutes
    refetchInterval: REFETCH_INTERVAL
  })

  const summaries = summariesRequest.data ?? EMPTY_SUMMARIES

  const hasAlreadyFetchedAndHasNoItems = summariesRequest.isFetched && isEmpty(summaries)

  const { isLoading: isLoading, isError, isFetching } = summariesRequest

  const isListView = 'view' in searchParams && searchParams.view === 'list'
  /**
   * Computing the visible summaries depending on the context
   * If search is active we're filtering the non-matching summaries
   * If the current metric is an irrigation metric we're showing only the current metric summaries
   */

  const { hiddenElementsCount, filteredResults } = useFilteredSummaries({
    universe,
    focusedMetricId,
    summaries,
    search
  })

  usePODClusters({
    summaries: filteredResults,
    isPlotsMap: true,
    isFetching: isFetching ?? false,
    summariesQueryCacheKey: summariesRequester.key
  })

  return preferencesQuery.isLoading ? (
    <LoadingCircle />
  ) : preferencesQuery.data?.onboardingContext?.shouldShowOnboarding === true ? (
    <Redirect
      to={preferencesQuery.data?.onboardingContext?.step ?? `/farms/${org.id}/onboarding/welcome`}
      search={preferencesQuery.data?.onboardingContext?.search}
    />
  ) : (
    <>
      {/* <ClusteringDebugPanel viewportCount={viewportCount} /> */}
      <PlotsMapOverlay
        allSummaries={summaries}
        currentItems={filteredResults}
        isLoading={isLoading}
      >
        <FeatGuarded org={org} feat='FEAT-WEATHERMAP-PLOT' mapFallback>
          {isListView ? (
            <>
              <MyFarmTools
                summaries={summaries}
                filteredResults={filteredResults}
                displaySettingsElement={<DisplaySettings />}
                isPlotsMap
              />
              <PlotOrStationList
                currentItems={filteredResults}
                focusedHorizon={focusedHorizon}
                hasAlreadyFetchedAndHasNoItems={hasAlreadyFetchedAndHasNoItems}
                hiddenElementsCount={hiddenElementsCount}
                isError={isError}
                isExpertOrg={isExpertOrg}
                isLoading={isLoading}
                summariesQueryCacheKey={summariesRequester.key}
              />
            </>
          ) : null}
        </FeatGuarded>
      </PlotsMapOverlay>
    </>
  )
}

export const Component = () => {
  return <RedesignedIndex />
}

export default Component
